import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useNotifications } from '@/contexts/NotificationContext';
import Icon from '@/components/atoms/Icon/Icon';
import Button from '@/components/atoms/Button';
import Card from '@/components/atoms/Card';
import { useDateFormatter } from '@/hooks/useDateFormatter';
import { usePriceFormat } from '@/hooks/usePriceFormat';
import { NotificationOut } from '@/types/api';
import './NotificationBell.scss';

const NotificationBell: React.FC = () => {
  const { t } = useTranslation();
  const { notifications, unreadCount, connected, markAsRead, markAllAsRead, deleteNotification } = useNotifications();
  const { formatRelativeTime } = useDateFormatter();
  const { formatPrice } = usePriceFormat();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  /**
   * Parse notification data field to extract order information
   */
  const parseNotificationData = (notification: NotificationOut) => {
    if (!notification.data) {
      return null;
    }

    try {
      const data = typeof notification.data === 'string' 
        ? JSON.parse(notification.data) 
        : notification.data;
      
      return data;
    } catch (error) {
      console.warn('Failed to parse notification data:', error);
      return null;
    }
  };

  /**
   * Get formatted message with order total if available
   */
  const getNotificationMessage = (notification: NotificationOut): string => {
    const parsedData = parseNotificationData(notification);
    
    // If notification has order data with total_amount, enhance the message
    if (parsedData && parsedData.total_amount && parsedData.total_amount > 0) {
      // Check if message already contains a total (might be wrong)
      const hasTotalInMessage = /\$?\s*\d+[.,]\d{2}/.test(notification.message) || 
                                /Total:\s*\$?\s*\d+[.,]\d{2}/i.test(notification.message);
      
      if (!hasTotalInMessage) {
        // Add total to message if not present
        return `${notification.message} ${t('notifications.total')}: ${formatPrice(parsedData.total_amount)}`;
      } else {
        // Replace incorrect total in message if present
        const correctedMessage = notification.message.replace(
          /Total:\s*\$?\s*[\d.,]+/i,
          `${t('notifications.total')}: ${formatPrice(parsedData.total_amount)}`
        );
        return correctedMessage;
      }
    }
    
    return notification.message;
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  const handleMarkAsRead = async (notificationId: number) => {
    try {
      await markAsRead(notificationId);
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const handleDelete = async (notificationId: number) => {
    try {
      await deleteNotification(notificationId);
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  return (
    <div className="notification-bell" ref={dropdownRef}>
      <button
        className={`notification-bell__button ${isOpen ? 'notification-bell__button--active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label={t('notifications.bell')}
        aria-expanded={isOpen}
      >
        <Icon name="campana" size={20} alt={t('notifications.bell')} variant='default' />
        {unreadCount > 0 && (
          <span className="notification-bell__badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
        {connected && (
          <span className="notification-bell__indicator notification-bell__indicator--connected" title={t('notifications.connected')} />
        )}
        {!connected && (
          <span className="notification-bell__indicator notification-bell__indicator--disconnected" title={t('notifications.disconnected')} />
        )}
      </button>

      {isOpen && (
        <div className="notification-bell__dropdown">
          <Card variant="elevated" className="notification-bell__card">
            <div className="notification-bell__header">
              <h3 className="notification-bell__title">
                {t('notifications.title')}
                {unreadCount > 0 && (
                  <span className="notification-bell__unread-count">({unreadCount})</span>
                )}
              </h3>
              {notifications.length > 0 && (
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={handleMarkAllAsRead}
                  className="notification-bell__mark-all-read"
                >
                  {t('notifications.markAllRead')}
                </Button>
              )}
            </div>

            <div className="notification-bell__list">
              {notifications.length === 0 ? (
                <div className="notification-bell__empty">
                  <Icon name="campana" size={48} alt={t('notifications.empty')} />
                  <p>{t('notifications.empty')}</p>
                </div>
              ) : (
                notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`notification-bell__item ${!notification.read ? 'notification-bell__item--unread' : ''}`}
                  >
                    <div className="notification-bell__item-content">
                      <div className="notification-bell__item-header">
                        <h4 className="notification-bell__item-title">{notification.title}</h4>
                        {!notification.read && (
                          <span className="notification-bell__item-unread-dot" />
                        )}
                      </div>
                      <p className="notification-bell__item-message">
                        {getNotificationMessage(notification)}
                      </p>
                      {(() => {
                        const parsedData = parseNotificationData(notification);
                        if (parsedData && parsedData.order_id && parsedData.total_amount) {
                          return (
                            <div className="notification-bell__item-order-info">
                              <span className="notification-bell__item-order-id">
                                {t('notifications.order')} #{parsedData.order_id}
                              </span>
                              <span className="notification-bell__item-order-total">
                                {t('notifications.total')}: {formatPrice(parsedData.total_amount)}
                              </span>
                            </div>
                          );
                        }
                        return null;
                      })()}
                      <div className="notification-bell__item-footer">
                        <span className="notification-bell__item-time">
                          {formatRelativeTime(notification.created_at)}
                        </span>
                        <div className="notification-bell__item-actions">
                          {!notification.read && (
                            <Button
                              variant="ghost"
                              size="xs"
                              onClick={() => handleMarkAsRead(notification.id)}
                              className="notification-bell__action-btn"
                            >
                              {t('notifications.markRead')}
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="xs"
                            onClick={() => handleDelete(notification.id)}
                            className="notification-bell__action-btn notification-bell__action-btn--delete"
                          >
                            {t('notifications.delete')}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
            {notifications.length > 0 && (
              <div className="notification-bell__footer">
                <Button
                  fullWidth
                  variant="secondary"
                  size="xs"
                  onClick={() => setIsOpen(false)}
                  className="notification-bell__mark-all-read-footer notification-bell__view-all"
                >
                  <Link to="/notifications" >
                    {t('notifications.viewAll')}
                  </Link>
                </Button>
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
